/**
 * Shared medicine form — used for both Add and Edit (A-07).
 *
 * Controlled form; the parent supplies an optional `initial` (edit mode) + an `onSubmit`
 * that performs the create/update mutation. Renders in a modal-style card.
 */

import { useState } from "react";

import type { Medicine, MedicineInput } from "../../api/admin";
import { unitLabel } from "../../lib/rxLines";
import { useUnitKinds } from "../../lib/useUnitKinds";
import { Select } from "../../components/ui/Select";

type FieldKey = "name" | "brand" | "unit" | "unit_kind" | "mrp" | "default_gst_pct" | "hsn_code";
type Errors = Partial<Record<FieldKey, string>>;

// Keep only digits and a single ".", with at most 2 decimal places (paise / GST fractions).
function toDecimal(v: string): string {
  const [int, ...rest] = v.replace(/[^\d.]/g, "").split(".");
  return rest.length ? `${int}.${rest.join("").slice(0, 2)}` : int;
}

// As the API takes them (ADM-09, D-24): name, unit and GST are required; brand, HSN and MRP are
// optional, so a second strength of one brand, or a legacy row with no MRP, can be saved. The form
// used to demand all three and refuse MRP 0.
function validate(f: MedicineInput): Errors {
  const errors: Errors = {};
  if (!f.name.trim()) errors.name = "Name is required";
  if (!f.unit.trim()) errors.unit = "Unit is required";
  if (!f.unit_kind) errors.unit_kind = "Unit kind is required";

  const mrp = (f.mrp ?? "").trim();
  if (mrp && (Number.isNaN(Number(mrp)) || Number(mrp) < 0)) errors.mrp = "Enter a valid amount, or leave it blank";

  const gst = (f.default_gst_pct ?? "").trim();
  if (!gst) errors.default_gst_pct = "GST % is required";
  else if (Number.isNaN(Number(gst))) errors.default_gst_pct = "Enter a valid percentage";
  else if (Number(gst) > 28) errors.default_gst_pct = "GST can be at most 28%";

  const hsn = (f.hsn_code ?? "").trim();
  if (hsn && !/^\d{2,8}$/.test(hsn)) errors.hsn_code = "HSN code must be 2 to 8 digits, or blank";

  return errors;
}

export function MedicineForm({
  initial,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  initial?: Medicine | null;
  submitting: boolean;
  error: string | null;
  onSubmit: (body: MedicineInput) => void;
  onCancel: () => void;
}) {
  // The units the API accepts, creams and chronic-care forms included (D-21).
  const unitKinds = useUnitKinds();
  // schedule_class is intentionally not surfaced in the UI; it defaults to "none" on the
  // backend, which keeps downstream compliance logic correct. We still send it so an edit
  // preserves any existing value rather than clearing it.
  const [form, setForm] = useState<MedicineInput>({
    name: initial?.name ?? "",
    brand: initial?.brand ?? "",
    schedule_class: initial?.schedule_class ?? "none",
    hsn_code: initial?.hsn_code ?? "",
    mrp: initial?.mrp ?? "",
    default_gst_pct: initial?.default_gst_pct ?? "12.00",
    unit: initial?.unit ?? "tablet",
    unit_kind: initial?.unit_kind ?? "tablet",
  });

  const set = (patch: Partial<MedicineInput>) => setForm({ ...form, ...patch });

  // Errors show only after a field is left or submit is attempted, not while typing.
  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const errors = validate(form);
  const errorFor = (k: FieldKey) => (submitAttempted || touched[k] ? errors[k] : undefined);
  const touch = (k: FieldKey) => () => setTouched((t) => ({ ...t, [k]: true }));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (Object.keys(errors).length > 0) {
      setSubmitAttempted(true);
      return;
    }
    onSubmit({
      ...form,
      name: form.name.trim(),
      // Blank brand / HSN mean none (an edit clears them); a blank MRP is ₹0.00, "not set".
      brand: (form.brand ?? "").trim(),
      hsn_code: (form.hsn_code ?? "").trim(),
      unit: form.unit.trim(),
      mrp: (form.mrp ?? "").trim() || "0.00",
      default_gst_pct: (form.default_gst_pct ?? "").trim(),
    });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Name" required error={errorFor("name")}>
          <input
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            onBlur={touch("name")}
            required
            aria-invalid={!!errorFor("name")}
            className={inputClass(errorFor("name"))}
          />
        </Field>
        <Field label="Brand (optional)" error={errorFor("brand")}>
          <input
            value={form.brand ?? ""}
            onChange={(e) => set({ brand: e.target.value })}
            onBlur={touch("brand")}
            aria-invalid={!!errorFor("brand")}
            className={inputClass(errorFor("brand"))}
          />
        </Field>
        <Field label="Unit (label)" required error={errorFor("unit")}>
          <input
            value={form.unit}
            onChange={(e) => set({ unit: e.target.value })}
            onBlur={touch("unit")}
            required
            aria-invalid={!!errorFor("unit")}
            className={inputClass(errorFor("unit"))}
          />
        </Field>
        <Field label="Unit kind" required error={errorFor("unit_kind")}>
          <Select
            value={form.unit_kind}
            options={(form.unit_kind && !unitKinds.includes(form.unit_kind) ? [form.unit_kind, ...unitKinds] : unitKinds).map((u) => ({ value: u, label: unitLabel(u) }))}
            onChange={(unit_kind) => set({ unit_kind })}
            onBlur={touch("unit_kind")}
            required
            invalid={!!errorFor("unit_kind")}
            className={`w-full rounded-sm py-1 pl-2 ${errorFor("unit_kind") ? "border-red-500" : ""}`}
          />
        </Field>
        <Field label="MRP (₹, optional)" error={errorFor("mrp")}>
          <input
            value={form.mrp ?? ""}
            onChange={(e) => set({ mrp: toDecimal(e.target.value) })}
            onBlur={touch("mrp")}
            inputMode="decimal"
            placeholder="e.g. 25.50"
            aria-invalid={!!errorFor("mrp")}
            className={inputClass(errorFor("mrp"))}
          />
        </Field>
        <Field label="Default GST %" required error={errorFor("default_gst_pct")}>
          <input
            value={form.default_gst_pct ?? ""}
            onChange={(e) => set({ default_gst_pct: toDecimal(e.target.value) })}
            onBlur={touch("default_gst_pct")}
            inputMode="decimal"
            placeholder="12.00"
            required
            aria-invalid={!!errorFor("default_gst_pct")}
            className={inputClass(errorFor("default_gst_pct"))}
          />
        </Field>
        <Field label="HSN code (optional)" error={errorFor("hsn_code")}>
          <input
            value={form.hsn_code ?? ""}
            onChange={(e) => set({ hsn_code: e.target.value.replace(/\D/g, "").slice(0, 8) })}
            onBlur={touch("hsn_code")}
            inputMode="numeric"
            maxLength={8}
            placeholder="2 to 8 digits"
            aria-invalid={!!errorFor("hsn_code")}
            className={inputClass(errorFor("hsn_code"))}
          />
        </Field>
      </div>
      {error && <div className="text-sm text-red-600">{error}</div>}
      <div className="flex gap-2">
        <button className="rounded-sm bg-brand px-4 py-1.5 text-brand-fg" disabled={submitting}>
          {initial ? "Save changes" : "Add medicine"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-sm border px-4 py-1.5">
          Cancel
        </button>
      </div>
    </form>
  );
}

function inputClass(error?: string) {
  return `w-full rounded-sm border px-2 py-1 ${error ? "border-red-500" : ""}`;
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-gray-500">
      <span>
        {label}
        {required && <span className="ml-0.5 text-red-600" aria-hidden="true">*</span>}
      </span>
      {children}
      {error && <span className="text-red-600">{error}</span>}
    </label>
  );
}
